import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Waves, Check, X, MapPin } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import FilmSection from "@/components/od/FilmSection";

const SITE = "https://oceandata.ca";

const MARQUEE = [
  "Menos papeleo", "Menos repetición", "Menos persecuciones",
  "Más control", "Un solo sistema simple", "Del cliente al pago",
];

const STEPS = [
  ["Comprender", "Observamos cómo circula realmente el trabajo en su empresa — una conversación, no una auditoría de seis semanas."],
  ["Simplificar", "Quitamos los pasos que ya no sirven a nadie: el reingreso de datos, las persecuciones, los reportes armados a mano."],
  ["Conectar", "Conectamos las herramientas que ya tiene — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk."],
  ["Automatizar", "El trabajo repetitivo se hace solo, y cada acción espera el sí de una persona."],
  ["Mejorar", "Medimos las horas recuperadas y pasamos al siguiente proceso — solo con evidencia."],
];

const BEFORE = [
  "La gente lo recuerda todo",
  "La información vive en los correos",
  "Excel entre cada paso",
  "Los seguimientos se hacen por teléfono",
  "Las cifras se reescriben a mano",
  "Los retrasos se descubren demasiado tarde",
];

const AFTER = [
  "El sistema lo recuerda",
  "El cliente se vuelve proyecto, luego factura",
  "El equipo sabe qué hacer hoy",
  "El trabajo avanza sin persecuciones",
  "La factura sale mientras el trabajo está fresco",
  "Lo que necesita su atención ya está en una lista",
];

const FAQ = [
  {
    q: "¿Tengo que cambiar de software?",
    a: "No. Conectamos lo que ya usa. Nadie reaprende su trabajo — la información por fin circula entre sus herramientas.",
  },
  {
    q: "¿Cuánto tarda un resultado?",
    a: "Un primer proceso conectado se mide en semanas. En Construction Horizon: respuestas a los RFI 43 % más rápidas en seis semanas, medido en sus propios registros.",
  },
  {
    q: "¿Cuánto cuesta empezar?",
    a: "Nada. La revisión de 15 minutos es gratuita, y cada proyecto empieza con una auditoría gratuita de análisis y mapeo de procesos (hasta 2 reuniones). Después, precio fijo: desde $899 USD (≈ $1,250 CAD) hasta $4,779 USD (≈ $6,600 CAD), más impuestos según el alcance.",
  },
  {
    q: "¿Dónde quedan mis datos?",
    a: "Con usted, en sus sistemas. Cada cambio espera su aprobación, y todo queda registrado en una pista de auditoría.",
  },
];

function MiamiBannerEs() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("od-geo-tracked")) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/geo`)
      .then((r) => {
        sessionStorage.setItem("od-geo-tracked", "1");
        if (r.data.miami) setShow(true);
      })
      .catch(() => {});
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="miami-geo-banner-es"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="bg-brand-navy text-white"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-4">
            <p className="inline-flex items-center gap-2 text-sm font-medium">
              <MapPin size={14} className="text-brand-gold shrink-0" />
              Negocios de Miami: devuélvale capacidad a su equipo.
            </p>
            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/es/miami"
                data-testid="miami-geo-banner-es-cta"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white text-xs font-semibold px-4 py-2 transition-colors duration-300 hover:bg-brand-tealhover"
              >
                Vea cómo <ArrowRight size={13} />
              </Link>
              <button
                data-testid="miami-geo-banner-es-close"
                onClick={() => setShow(false)}
                aria-label="Cerrar"
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const ES_LINKS = [
  { label: "Miami: calculadora de capacidad para su industria", href: "/es/miami" },
  { label: "Mantenga sus herramientas. Las conectamos.", href: "/es/mantenga-sus-herramientas" },
  { label: "Respuesta a los RFI en horas", href: "/es/respuesta-rfi-en-horas" },
  { label: "La revisión gratuita de 15 minutos", href: "/es/revision-gratuita-15-minutos" },
  { label: "Caso de estudio: Construction Horizon (43 %)", href: "/es/journal/horizon-construction-caso-de-estudio" },
  { label: "Precios: un precio claro, capacidad medida", href: "/es/precios" },
  { label: "Cómo funciona: de la auditoría gratuita a la capacidad medida", href: "/es/como-funciona" },
  { label: "Soluciones: reparar, conectar, automatizar", href: "/es/soluciones" },
  { label: "Acerca de Bruno Pouliot", href: "/es/nosotros" },
  { label: "Demos en vivo", href: "/es/demos" },
];

export default function EsHomePage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Ocean Data — Más capacidad. Mejor trabajo. | Miami — Montréal";
    document.documentElement.lang = "es";
    const links = [];
    const add = (hreflang, href) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    };
    add("es", `${SITE}/es/`);
    add("en-CA", `${SITE}/`);
    add("fr-CA", `${SITE}/fr/`);
    add("x-default", `${SITE}/`);
    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/es/`,
        name: "Ocean Data — Más capacidad. Mejor trabajo.",
        description:
          "Ocean Data conecta las herramientas que su empresa ya usa en un solo sistema simple, del cliente al pago. Menos papeleo, menos persecuciones, más control. Revisión gratuita de 15 minutos — Miami, Montréal y todo Quebec.",
        inLanguage: "es",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "FAQPage",
        inLanguage: "es",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="es">
      <MiamiBannerEs />
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Ocean Data · Construcción y servicios profesionales · Miami — Montréal
          </p>
          <h1
            data-testid="eshome-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="eshome-headline-line-1">Más capacidad.</MaskedLine>
            <MaskedLine delay={0.3} testId="eshome-headline-line-2">
              <span className="text-brand-teal">Mejor trabajo.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Conectamos el trabajo que su equipo hace cada día — del cliente al
            pago — en un solo sistema simple. Menos papeleo. Menos repetición.
            Menos persecuciones.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="eshome-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Muéstreme dónde simplificar
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Revisión gratuita de 15 minutos · Sin compromiso</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 inline-flex items-center gap-3 rounded-full bg-brand-goldsoft border border-brand-gold/30 px-5 py-2.5"
          >
            <span className="w-2 h-2 rounded-full bg-brand-gold" />
            <p className="text-xs sm:text-sm font-semibold text-brand-golddeep">
              25–40 % menos tiempo administrativo · 647 implementaciones desde 2017
            </p>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-6 border-y border-slate-200/70 overflow-hidden bg-white" aria-hidden="true">
        <div className="od-marquee-track flex whitespace-nowrap">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-6 font-display font-semibold text-lg text-brand-navy/80 inline-flex items-center gap-6">
              {m} <span className="text-brand-gold">✳</span>
            </span>
          ))}
        </div>
      </div>

      {/* 5 pasos */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Cómo funciona
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Cinco pasos. Un proceso a la vez.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {STEPS.map(([title, body], i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div data-testid={`eshome-step-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-6 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5">
                  <p className="font-mono text-sm text-brand-teal font-semibold mb-3">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="font-display font-semibold text-lg text-brand-navy mb-2.5">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Antes / Después */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Antes / Después
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Antes, la gente lo recuerda. Después, el sistema lo recuerda.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            <Reveal>
              <div data-testid="eshome-before" className="rounded-2xl bg-white border border-slate-200 p-7 sm:p-8 h-full">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold mb-6">Cómo funciona hoy</p>
                <ul className="space-y-4">
                  {BEFORE.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-600">
                      <X size={16} className="text-slate-400 shrink-0 mt-1" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div data-testid="eshome-after" className="rounded-2xl bg-white border-2 border-brand-green/40 p-7 sm:p-8 h-full">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-green font-semibold mb-6">Con Ocean Data</p>
                <ul className="space-y-4">
                  {AFTER.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                      <Check size={16} className="text-brand-green shrink-0 mt-1" /> {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Prueba */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Resultados medidos, no promesas
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {[
              ["647", "implementaciones de Zoho One desde 2017"],
              ["25–40 %", "menos tiempo administrativo, medido en nuestros clientes"],
              ["43 %", "respuestas a los RFI más rápidas en Construction Horizon"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`eshome-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <p className="font-display font-medium text-lg leading-snug text-white max-w-2xl">
                «En seis semanas, el tiempo de respuesta de nuestro equipo a los RFI cayó 43 %. Las decisiones ahora toman horas, no días.»
                <span className="block mt-2 text-xs text-slate-400 font-sans">Director de Operaciones, Construction Horizon</span>
              </p>
              <Link
                to="/es/journal/horizon-construction-caso-de-estudio"
                data-testid="eshome-case-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 text-brand-gold text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
              >
                Leer el caso de estudio <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Herramientas */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-8">
            Conectado a las herramientas que ya usa
          </p>
          <p className="font-display font-semibold text-xl sm:text-2xl text-brand-navy/60 tracking-wide">
            Zoho One · Zoho CRM · Zoho Projects · Zoho Books · QuickBooks · Microsoft 365 · Google Workspace · Excel · Autodesk
          </p>
        </div>
      </section>

      <FilmSection lang="es" />

      {/* Explorar en español */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Explorar en español
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-10">
            Por dónde empezar su lectura
          </h2>
          <div className="space-y-3">
            {ES_LINKS.map((l, i) => (
              <Reveal key={l.href} delay={i * 0.05}>
                <Link
                  to={l.href}
                  data-testid={`eshome-link-${i}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl bg-white border border-slate-200 px-7 py-5 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <span className="font-display font-semibold text-base sm:text-lg text-brand-navy group-hover:text-brand-teal transition-colors">
                    {l.label}
                  </span>
                  <ArrowRight size={17} className="text-brand-teal shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Preguntas frecuentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Lo que nos preguntan antes de empezar
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`eshome-faq-${i}`} className="group rounded-2xl bg-[#F8FAFC] border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <span className="inline-flex w-11 h-11 rounded-full bg-white/10 text-brand-pale items-center justify-center mb-5">
                  <Waves size={18} />
                </span>
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Vea los dos o tres lugares donde simplificar primero.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Una revisión gratuita de 15 minutos. Una respuesta personal en
                  un día hábil. Nada que cancelar.
                </p>
              </div>
              <button
                data-testid="eshome-cta-final"
                onClick={() => setOpen(true)}
                className="shrink-0 inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                Muéstreme dónde simplificar
                <ArrowRight size={18} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
